import React, { useMemo } from "react";
import { map } from "lodash";
import DoubleTimeChart from "@charty/double-time-chart";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizDoubleTimeChart } from "@radarly/models/dataviz";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import { useRangeWithTimeUnit, useTimezoneFromFragment } from "../../data";
import Empty from "../../empty";

export interface SharedWidgetsMetricsDoubleTimeChartInnerProps {
  loading: boolean;
  data: SearchFragmentDateMetrics["data"];
  fragment: SearchFragmentDateMetrics;
  dataviz: DatavizDoubleTimeChart;
  onClickLegend?: (uid: string | number) => void;
  onClickItem?: (uid: string | number, range: { from: Date; to: Date }) => void;
  className?: string;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

function getTypeFromMetric(metric: Metric) {
  return {
    [Metric.Doc]: "bar",
    [Metric.Repost]: "bar",
    [Metric.Reach]: "line",
    [Metric.Engagement]: "line",
  }[metric];
}
function getData(
  buckets: SearchFragmentDateMetrics["data"]["aggs"]["date"]["values"],
  metric: Metric
) {
  return map(buckets, (bucket) => {
    if (bucket) {
      return {
        date: bucket.key,
        count: bucket.counts[metric],
      };
    } else {
      return null;
    }
  });
}

export default function SharedWidgetsMetricsDoubleTimeChartInner({
  loading,
  data,
  fragment,
  dataviz,
  onClickItem,
  onClickLegend,
  className,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsMetricsDoubleTimeChartInnerProps) {
  const range = useRangeWithTimeUnit(fragment, loading, nowDate);
  const locale = useCurrentUserLocale();
  const metrics = fragment.request.aggs.date.metrics;
  const timezone = useTimezoneFromFragment(fragment);
  const series = useMemo(() => {
    const availableSeries = map(metrics, (metric) => {
      return {
        id: metric,
        label: metric,
        data: getData(data ? data.aggs.date.values : null, metric),
        type: getTypeFromMetric(metric),
      };
    });
    return availableSeries;
  }, [data, metrics]);
  return (
    <Empty empty={!series.length}>
      <DoubleTimeChart
        series={series}
        range={range}
        locale={locale}
        className={className}
        onClickItem={onClickItem}
        onClickLegend={onClickLegend}
        scale={dataviz.scale || "linear"}
        timezone={timezone}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
        displayVolumes={dataviz.displayVolumes}
      />
    </Empty>
  );
}
